import { useMemo, useState, useCallback } from 'react';
import { Goal, HabitFrequency } from '@/types/goal';
import { GoalCard } from '@/components/GoalCard';
import { CreateGoalDialog } from '@/components/CreateGoalDialog';
import { StatsHeader } from '@/components/StatsHeader';
import { TabStatsLine } from './TabStatsLine';
import { StreakMilestoneModal } from '@/components/StreakMilestoneModal';
import { GoalCompleteCelebration } from '@/components/goals/GoalCompleteCelebration';
import { CompletedGoalCard } from '@/components/goals/CompletedGoalCard';
import { Sparkles, Target, TrendingUp, CheckCircle2, Lock, ArrowRight, ChevronDown, Trophy } from 'lucide-react';
import { HabitToggleResult } from './HabitTrackingTab';
import { useProfile } from '@/hooks/useProfile';
import { ProfileEnrichmentStep } from '@/components/onboarding/ProfileEnrichmentStep';
import { GoalSuggestionsOnboarding } from '@/components/onboarding/GoalSuggestionsOnboarding';
import { getTierFromProfile, TIER_LIMITS } from '@/lib/tier-utils';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useNavigate } from 'react-router-dom';
import { BETA_CONFIG } from '@/config/beta';
import { ValuesGateBanner } from '@/components/journey/ValuesGateBanner';

interface GoalSettingTabProps {
  goals: Goal[];
  completedGoals: Goal[];
  valuesComplete?: boolean;
  onNavigateToValues?: () => void;
  onAddGoal: (title: string, description: string, eTypes: any[], targetDate?: Date) => Promise<Goal | null>;
  onUpdateGoal: (id: string, updates: Partial<Goal>) => Promise<void>;
  onDeleteGoal: (id: string) => Promise<void>;
  onCompleteGoal: (id: string) => Promise<boolean>;
  onAddHabit: (goalId: string, title: string, frequency?: HabitFrequency, scheduleDays?: number | null, linkedActionId?: string) => Promise<void>;
  onToggleHabit: (goalId: string, habitId: string) => Promise<HabitToggleResult | void>;
  totalStreak: number;
  todayProgress: number;
  onNavigateToPlan?: (milestoneIndex: number) => void;
  onTabChange?: (tab: string) => void;
}

export const GoalSettingTab = ({
  goals,
  completedGoals,
  valuesComplete,
  onNavigateToValues,
  onAddGoal,
  onUpdateGoal,
  onDeleteGoal,
  onCompleteGoal,
  onAddHabit,
  onToggleHabit,
  totalStreak,
  todayProgress,
  onNavigateToPlan,
  onTabChange,
}: GoalSettingTabProps) => {
  const { profile } = useProfile();
  const navigate = useNavigate();
  const tier = getTierFromProfile(profile);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationGoalTitle, setCelebrationGoalTitle] = useState('');
  const [completedHistoryOpen, setCompletedHistoryOpen] = useState(false);
  const [skipEnrichment, setSkipEnrichment] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showAllGoals, setShowAllGoals] = useState(false);

  const activeGoalsCount = goals.filter(g => g.progress < 100).length;
  const limit = TIER_LIMITS[tier].max_goals;
  const isAtLimit = !BETA_CONFIG.allFeaturesUnlocked && tier === 'free' && activeGoalsCount >= limit;
  const showUsage = !BETA_CONFIG.allFeaturesUnlocked && tier === 'free';

  const handleCompleteGoal = useCallback(async (goalId: string, goalTitle: string) => {
    const success = await onCompleteGoal(goalId);
    if (success) {
      setCelebrationGoalTitle(goalTitle);
      setShowCelebration(true);
    }
  }, [onCompleteGoal]);

  // Calculate stats for the tab header
  const stats = useMemo(() => {
    const activeGoals = goals.filter(g => g.progress < 100).length;
    const avgProgress = goals.length > 0
      ? Math.round(goals.reduce((sum, g) => sum + g.progress, 0) / goals.length)
      : 0;

    return {
      activeGoals,
      completedGoalCount: completedGoals.length,
      avgProgress,
    };
  }, [goals, completedGoals]);

  if (goals.length === 0 && completedGoals.length === 0) {
    // Pre-values users see a gate banner + preview of what they'll get
    if (!valuesComplete) {
      return (
        <div>
          <ValuesGateBanner onNavigateToValues={() => onNavigateToValues?.()} />
          <div className="text-center py-12 space-y-4 opacity-60">
            <div className="inline-flex p-5 rounded-full bg-gradient-to-br from-primary/10 to-accent/10">
              <Target className="w-10 h-10 text-primary/60" />
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-display font-bold">Set Goals Using the 3E Framework</h2>
              <p className="text-muted-foreground max-w-md mx-auto text-sm">
                Create goals across Education, Exposure, and Experience categories — aligned to your core values for maximum impact.
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Profile enrichment step: show before goal suggestions if no job role set
    if (!profile?.job_role && !skipEnrichment) {
      return (
        <ProfileEnrichmentStep
          onComplete={() => setShowSuggestions(true)}
          onSkip={() => setSkipEnrichment(true)}
        />
      );
    }

    // Auto-show suggestions when arriving with profile complete
    if (!showSuggestions) setShowSuggestions(true);

    // AI-guided goal suggestions (profile is complete or user skipped enrichment)
    return (
      <GoalSuggestionsOnboarding
        onAddGoal={onAddGoal}
        onDeleteGoal={onDeleteGoal}
        onTabChange={onTabChange}
        onDone={() => setShowSuggestions(false)}
      />
    );
  }

  // Keep showing suggestions until user explicitly dismisses
  if (showSuggestions) {
    return (
      <GoalSuggestionsOnboarding
        onAddGoal={onAddGoal}
        onDeleteGoal={onDeleteGoal}
        onTabChange={onTabChange}
        onDone={() => setShowSuggestions(false)}
        existingGoalCount={goals.length}
      />
    );
  }

  if (goals.length === 0) {
    return (
      <>
        <div className="space-y-6">
          <div className="text-center py-12 space-y-6">
            <div className="inline-flex p-6 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 animate-float">
              <Trophy className="w-12 h-12 text-yellow-500" />
            </div>
            <div className="space-y-2">
              <h2 className="text-2xl font-display font-bold">All Goals Completed!</h2>
              <p className="text-muted-foreground max-w-md mx-auto">
                Amazing work! Ready to set a new goal and keep the momentum going?
              </p>
            </div>
            <CreateGoalDialog onCreateGoal={onAddGoal} />
          </div>

          {/* Completed Goals History */}
          <Collapsible open={completedHistoryOpen} onOpenChange={setCompletedHistoryOpen}>
            <CollapsibleTrigger asChild>
              <button className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors w-full group">
                <Trophy className="w-4 h-4 text-yellow-500" />
                <span className="font-medium">Completed Goals ({completedGoals.length})</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${completedHistoryOpen ? 'rotate-180' : ''}`} />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-3">
              <div className="grid gap-3 lg:grid-cols-2">
                {completedGoals.map((goal) => (
                  <CompletedGoalCard key={goal.id} goal={goal} />
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
        <GoalCompleteCelebration
          isOpen={showCelebration}
          onClose={() => setShowCelebration(false)}
          goalTitle={celebrationGoalTitle}
          completedCount={completedGoals.length}
        />
      </>
    );
  }

  return (
    <div className="space-y-6">
      <StatsHeader totalGoals={goals.length} totalStreak={totalStreak} todayProgress={todayProgress} />
      
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Target className="w-5 h-5 text-primary" />
              Your Goals
            </h2>
            {showUsage && (
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                isAtLimit
                  ? 'bg-destructive/10 text-destructive'
                  : 'bg-muted text-muted-foreground'
              }`}>
                {activeGoalsCount}/{limit} used {isAtLimit && <Lock className="inline w-3 h-3 ml-0.5" />}
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            Track progress across Education, Exposure, and Experience
          </p>
          <TabStatsLine
            stats={[
              { value: stats.activeGoals, label: 'active goals', icon: <Target className="w-3.5 h-3.5" /> },
              { value: stats.completedGoalCount, label: 'completed', icon: <CheckCircle2 className="w-3.5 h-3.5" /> },
              { value: `${stats.avgProgress}%`, label: 'avg progress', icon: <TrendingUp className="w-3.5 h-3.5" /> },
            ]}
          />
          {isAtLimit && (
            <Button
              variant="link"
              size="sm"
              className="text-primary p-0 h-auto mt-1"
              onClick={() => navigate('/pricing')}
            >
              Upgrade for unlimited goals <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          )}
        </div>
        <div className="shrink-0">
          <CreateGoalDialog onCreateGoal={onAddGoal} />
        </div>
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        {(showAllGoals ? goals : goals.slice(0, 4)).map((goal) => (
          <GoalCard
            key={goal.id}
            goal={goal}
            onToggleHabit={(habitId) => onToggleHabit(goal.id, habitId)}
            onAddHabit={(title, frequency, scheduleDays, linkedActionId) => onAddHabit(goal.id, title, frequency, scheduleDays, linkedActionId)}
            onDelete={() => onDeleteGoal(goal.id)}
            onComplete={() => handleCompleteGoal(goal.id, goal.title)}
            onUpdateTitle={(title) => onUpdateGoal(goal.id, { title })}
            onNavigateToPlan={onNavigateToPlan}
          />
        ))}
      </div>
      {goals.length > 4 && (
        <div className="flex justify-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAllGoals(!showAllGoals)}
            className="text-muted-foreground hover:text-foreground"
          >
            {showAllGoals ? 'Show less' : `Show all ${goals.length} goals`}
          </Button>
        </div>
      )}

      {/* Completed Goals History */}
      {completedGoals.length > 0 && (
        <Collapsible open={completedHistoryOpen} onOpenChange={setCompletedHistoryOpen}>
          <CollapsibleTrigger asChild>
            <button className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors w-full group">
              <Trophy className="w-4 h-4 text-yellow-500" />
              <span className="font-medium">Completed Goals ({completedGoals.length})</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${completedHistoryOpen ? 'rotate-180' : ''}`} />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-3">
            <div className="grid gap-3 lg:grid-cols-2">
              {completedGoals.map((goal) => (
                <CompletedGoalCard key={goal.id} goal={goal} />
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Celebration Modal */}
      <GoalCompleteCelebration
        isOpen={showCelebration}
        onClose={() => setShowCelebration(false)}
        goalTitle={celebrationGoalTitle}
        completedCount={completedGoals.length}
      />
    </div>
  );
};
